import { useEffect, useRef } from 'react';

// reactbits "splash-cursor": a lightweight canvas fluid/splash trail that follows
// the cursor. Renders as an absolutely-positioned overlay filling its PARENT, so
// the parent must be `position: relative`. pointer-events are disabled so it never
// blocks clicks. Scoped intentionally to footers only.
export default function SplashCursor({ colors = ['#0D9488', '#22d3ee', '#a7f3d0'], maxParticles = 260 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const parent = canvas.parentElement;
    const ctx = canvas.getContext('2d');
    let raf = 0;
    let w = 0, h = 0, dpr = Math.min(window.devicePixelRatio || 1, 2);
    const particles = [];
    let seed = 1;
    const rnd = () => { seed = (seed * 1103515245 + 12345) & 0x7fffffff; return seed / 0x7fffffff; }; // deterministic-ish

    const resize = () => {
      const r = parent.getBoundingClientRect();
      w = r.width; h = r.height;
      canvas.width = Math.max(1, Math.floor(w * dpr));
      canvas.height = Math.max(1, Math.floor(h * dpr));
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(parent);

    let lastX = 0, lastY = 0, hasLast = false;
    const spawn = (x, y, speed) => {
      const count = Math.min(4, 1 + Math.floor(speed / 8));
      for (let i = 0; i < count; i++) {
        if (particles.length >= maxParticles) particles.shift();
        const ang = rnd() * Math.PI * 2;
        const spd = 0.4 + rnd() * (1.2 + speed * 0.06);
        particles.push({
          x, y,
          vx: Math.cos(ang) * spd,
          vy: Math.sin(ang) * spd - 0.3,
          life: 1,
          decay: 0.012 + rnd() * 0.02,
          r: 8 + rnd() * 22,
          color: colors[Math.floor(rnd() * colors.length)],
        });
      }
    };

    const onMove = (e) => {
      const r = parent.getBoundingClientRect();
      const x = e.clientX - r.left;
      const y = e.clientY - r.top;
      if (x < 0 || y < 0 || x > w || y > h) { hasLast = false; return; }
      const speed = hasLast ? Math.hypot(x - lastX, y - lastY) : 0;
      spawn(x, y, speed);
      lastX = x; lastY = y; hasLast = true;
    };
    window.addEventListener('pointermove', onMove, { passive: true });

    const frame = () => {
      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'lighter';
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.x += p.vx; p.y += p.vy; p.vy += 0.02; p.vx *= 0.98; p.vy *= 0.98;
        p.life -= p.decay;
        if (p.life <= 0) { particles.splice(i, 1); continue; }
        const rad = p.r * (0.6 + p.life * 0.9);
        const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, rad);
        g.addColorStop(0, hexA(p.color, 0.55 * p.life));
        g.addColorStop(1, hexA(p.color, 0));
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(p.x, p.y, rad, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalCompositeOperation = 'source-over';
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('pointermove', onMove);
    };
  }, [colors, maxParticles]);

  return (
    <canvas ref={canvasRef}
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 0 }} />
  );
}

// color helper: turn a #rrggbb into rgba with a given alpha
function hexA(hex, a) {
  const c = hex.replace('#', '');
  const n = c.length === 3 ? c.split('').map((x) => x + x).join('') : c;
  const r = parseInt(n.slice(0, 2), 16), g = parseInt(n.slice(2, 4), 16), b = parseInt(n.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}
